import React, { useEffect, useState } from 'react';
import { dataService } from '../../../services/dataService';
import { Student, LearningPath, Achievement, Game } from '../../../data/types';
import Card from '../../ui/Card';
import Button from '../../ui/Button';
import Badge from '../../ui/Badge';
import Avatar from '../../ui/Avatar';
import StudentLayout from '../../layouts/StudentLayout';
import { standardDetailsMap } from '../../../data/mockStudentLearningPaths';
import { mockStandards } from '../../../data/mockStandards';

// Helper to get standard status and action
function getStandardStatusAndAction(
  progress: import('../../../data/types').StandardProgress | undefined,
  standardId: string,
  locked: boolean
): { status: string; action: React.ReactNode } {
  if (locked) return { status: 'Locked', action: <Badge variant="secondary">Locked</Badge> };

  if (!progress) return { status: 'Not Started', action: <Badge variant="secondary">Not Started</Badge> };

  switch (progress.learningPhaseStatus) {
    case 'Completed':
      return { status: 'Completed', action: <Badge variant="success">Completed</Badge> };

    case 'PracticeCompleted':
      return { status: 'Practice', action: <Button size="sm" variant="primary">Practice</Button> };

    case 'VideoCompleted':
      return { status: 'In Progress', action: <Button size="sm" variant="primary">Continue</Button> };

    case 'NotStarted':
    default:
      return { status: 'Not Started', action: <Badge variant="secondary">Not Started</Badge> };

  }
}

const levelDefinitions = [
  { threshold: 0, name: "Math Novice" },
  { threshold: 100, name: "Number Explorer" },
  { threshold: 250, name: "Calculation Cadet" },
  { threshold: 500, name: "Problem Solver" },
  { threshold: 1000, name: "Math Adventurer" },
  { threshold: 2000, name: "Formula Master" },
  { threshold: 3500, name: "Math Wizard" },
  { threshold: 5000, name: "Math Champion" },
  { threshold: 7500, name: "Math Superhero" },
  { threshold: 10000, name: "Math Legend" }
];

function getAvatarEmoji(name: string) {
  // Simple emoji based on first character
  const emojis = ['🦉','🐧','🦄','🐯','🐼','🐸','🦊','🐵','🐶','🐱','🦁','🐨','🐻','🐰','🐮','🐷','🐔','🐙','🦋','🐢'];
  if (!name) return '🦉';
  const charCode = name.charCodeAt(0);
  return emojis[charCode % emojis.length];
}

function formatDate(dateString?: string) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString();
}

const StudentDashboard: React.FC = () => {
  const [student, setStudent] = useState<Student | null>(null);
  const [learningPaths, setLearningPaths] = useState<LearningPath[]>([]);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [level, setLevel] = useState<{name: string; current: number; next: number; progress: number}>({name: 'Math Novice', current: 1, next: 2, progress: 0});
  const [leaderboard, setLeaderboard] = useState<{name: string, rank: number, points: number}[]>([]);
  const [engagementMessage, setEngagementMessage] = useState<{message: string}>({message: 'Complete your daily math challenge to earn bonus points!'});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let studentId = localStorage.getItem('activeStudentId');
        const students = await dataService.getStudents();
        let currentStudent = studentId ? students.find(s => s.id === studentId) : students[0];
        if (!currentStudent) currentStudent = students[0];
        setStudent(currentStudent);

        // Level calculation
        const totalPoints = currentStudent.totalPoints || 0;
        let currentLevel = levelDefinitions[0];
        let nextLevel = levelDefinitions[1];
        for (let i = levelDefinitions.length - 1; i >= 0; i--) {
          if (totalPoints >= levelDefinitions[i].threshold) {
            currentLevel = levelDefinitions[i];
            nextLevel = levelDefinitions[i + 1] || levelDefinitions[i];
            break;
          }
        }
        const progress = nextLevel === currentLevel ? 100 : ((totalPoints - currentLevel.threshold) / (nextLevel.threshold - currentLevel.threshold)) * 100;
        setLevel({
          name: currentLevel.name,
          current: levelDefinitions.indexOf(currentLevel) + 1,
          next: levelDefinitions.indexOf(nextLevel) + 1,
          progress: progress
        });

        // Learning paths
        const studentPaths = await dataService.getStudentLearningPaths(currentStudent.id);
        setLearningPaths(studentPaths);

        // Achievements
        setAchievements(currentStudent.achievements || []);

        // Leaderboard
        setLeaderboard([
          { name: "MathWhiz", rank: 1, points: totalPoints + 120 },
          { name: "NumberNinja", rank: 2, points: totalPoints + 85 },
          { name: "AlgebraAce", rank: 3, points: totalPoints + 50 },
          { name: "You", rank: 4, points: totalPoints },
          { name: "MathExplorer", rank: 5, points: totalPoints - 30 }
        ]);

        // Engagement message
        const messages = [
          { message: 'Complete your daily math challenge to earn bonus points!' },
          { message: 'You\'re on a 5-day streak! Keep it up!' },
          { message: 'New achievements are waiting to be unlocked!' },
          { message: 'Your teacher just assigned a new learning path!' },
          { message: 'You\'re making great progress on fractions!' },
        ];
        setEngagementMessage(messages[Math.floor(Math.random() * messages.length)]);

        setLoading(false);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setLoading(false);
      }
   };

  fetchData();
}, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-bounce text-4xl text-purple-600">
          Loading your amazing math world...
        </div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="text-center p-8">
        <h2 className="text-2xl font-bold text-red-500">Oops!</h2>
        <p>We couldn't find your student information. Please try again!</p>
      </div>
    );
  }

  // Classic dashboard layout below (existing/classic style)
  return (
    <StudentLayout>
      <div className="p-4 md:p-6">
        {/* Welcome Header + Achievements Showcase */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 flex flex-col md:flex-row items-center">
          <div className="relative">
            <Avatar 
              src={student.avatarUrl || "https://placehold.co/100x100/6366f1/ffffff?text=Hi!"} 
              alt={student.name} 
              size="xl" 
            />
            <div className="absolute -bottom-2 -right-2 bg-yellow-400 text-white rounded-full w-10 h-10 flex items-center justify-center text-sm font-bold border-2 border-white">
              {student.grade}
            </div>
          </div>
          <div className="ml-0 md:ml-6 mt-4 md:mt-0 text-center md:text-left w-full flex flex-col md:flex-row md:items-center justify-between">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-blue-500">
                Hi, {student.name}!
              </h1>
              <p className="text-gray-600 text-lg mt-1">{engagementMessage.message}</p>
              <div className="mt-2 flex flex-wrap justify-center md:justify-start gap-2">
                <Badge variant="primary">
                  <span className="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    {student.totalPoints} Points
                  </span>
                </Badge>
                <Badge variant="success">
                  <span className="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Grade {student.grade}
                  </span>
                </Badge>
              </div>
              {/* Level Progress Bar */}
              <div className="mt-4 w-full max-w-xs">
                <div className="flex justify-between text-xs font-semibold text-gray-500 mb-1">
                  <span>{level.name}</span>
                  <span>Level {level.current} → {level.next}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div className="bg-blue-500 h-3 rounded-full transition-all duration-500" style={{ width: `${level.progress}%` }}></div>
                </div>
              </div>
            </div>
            {/* Achievements Showcase */}
            <div className="flex flex-col md:ml-8 mt-4 md:mt-0 items-center md:items-end">
              <div className="flex items-center space-x-2">
                {achievements.slice(0, 3).map(achievement => (
                  <div key={achievement.id} className="flex flex-col items-center">
                    <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center mb-1">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                      </svg>
                    </div>
                    <div className="text-xs text-gray-600 text-center font-medium max-w-[80px] truncate">
                      {achievement.name}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Leaderboard Preview */}
        <h2 className="text-2xl font-bold mb-4 text-green-600 flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          Class Leaderboard
        </h2>
        <div className="bg-white rounded-xl shadow-md overflow-hidden mb-8">
          <div className="p-4 bg-gradient-to-r from-green-500 to-teal-500 text-white">
            <h3 className="font-bold">Top Performers This Week</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {leaderboard.map((entry, index) => (
              <div key={index} className="flex items-center p-4">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold mr-3 ${index === 0 ? 'bg-yellow-400' : index === 1 ? 'bg-gray-300' : index === 2 ? 'bg-amber-700' : 'bg-green-500'}`}>
                  {index < 3 ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 17.75l-6.172 3.245 1.179-6.873-5-4.873 6.9-1.004L12 2.25l3.093 6.995 6.9 1.004-5 4.873 1.179 6.873z" />
                    </svg>
                  ) : (
                    entry.rank
                  )}
                </div>
                <div className="w-10 h-10 mr-2 rounded-full overflow-hidden border-2 border-white flex items-center justify-center bg-blue-100">
                  <span className="text-xl select-none">
                    {getAvatarEmoji(entry.name)}
                  </span>
                </div>
                <div className="flex-grow">
                  <h4 className="font-bold">{entry.name}</h4>
                  <p className="text-xs text-gray-500">{entry.rank === 4 ? 'You' : `Rank ${entry.rank}`}</p>
                </div>
                <div className="text-right">
                  <div className="font-bold text-green-600">{entry.points} pts</div>
                </div>
              </div>
            ))}
          </div>
          <div className="p-3 bg-gray-50 text-center">
            <Button variant="primary" className="w-full bg-primary">
              View Full Leaderboard
            </Button>
          </div>
        </div>

        {/* Learning Paths Section */}
        <h2 className="text-2xl font-bold mb-4 text-blue-600">Your Learning Paths</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {learningPaths.map((path) => (
            <Card key={path.id} className="mb-4">
              <div className="flex items-center mb-2">
                <h3 className="text-lg font-bold text-gray-800 flex-1">{path.name}</h3>
                <Badge variant="info">{path.subject}</Badge>
              </div>
              <div className="text-sm text-gray-500 mb-2">{path.description}</div>
              <div className="flex flex-wrap gap-2 mt-2">
                {path.standards && path.standards.map((standard, idx) => {
  // Find the standard details from mockStandards
  const standardDetail = mockStandards.find(s => s.id === standard.standardId);
  const standardName = standardDetail ? (standardDetail.title || standardDetail.code) : `Standard ${idx+1}`;
  // Determine if this standard is locked (e.g., previous standards must be completed)
  let isLocked = false;
  if (idx > 0) {
    const prevStandard = path.standards[idx - 1];
    const prevProgress = student.progress ? student.progress[prevStandard.standardId] : undefined;
    isLocked = !prevProgress || prevProgress.learningPhaseStatus !== 'Completed';
  }
  return (
    <div key={standard.standardId} className="flex flex-col items-center p-2">
      <div className="text-xs font-semibold text-gray-700 mb-1">{standardName}</div>
      {/* Classic: just show status badge */}
      <div>
        {getStandardStatusAndAction(student.progress ? student.progress[standard.standardId] : undefined, standard.standardId, isLocked).action}
      </div>
    </div>
  );
})}
              </div>
            </Card>
          ))}
        </div>

        {/* Achievements Section */}
        <h2 className="text-2xl font-bold mb-4 text-yellow-600 flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
          </svg>
          My Achievements
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
          {achievements.map(achievement => (
            <div key={achievement.id} className="bg-white rounded-xl shadow-md p-4 flex items-center">
              <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center mr-4 flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <div className="flex-grow">
                <h3 className="font-bold text-gray-800">{achievement.name}</h3>
                <p className="text-sm text-gray-600">{achievement.description}</p>
                <div className="mt-1 text-xs text-yellow-600 font-semibold">
                  +{achievement.points} points
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </StudentLayout>
  );
};

export default StudentDashboard;
